// src/utils/fingerprint.ts
// Browser fingerprinting utility for user analytics and ban system
// Generates unique browser fingerprint using multiple signals
/**
 * Generate canvas fingerprint
 * Different browsers/GPUs render canvas slightly differently
 */
function getCanvasFingerprint() {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return 'no-canvas';
        canvas.width = 200;
        canvas.height = 50;
        // Draw text with multiple styles
        ctx.textBaseline = 'top';
        ctx.font = '14px "Arial"';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('GridChat ðŸŽ¬', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('GridChat ðŸŽ¬', 4, 17);
        // Draw shapes
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgb(255,0,255)';
        ctx.beginPath();
        ctx.arc(50, 25, 20, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'rgb(0,255,255)';
        ctx.beginPath();
        ctx.arc(100, 25, 20, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();
        return canvas.toDataURL();
    }
    catch (e) {
        return 'canvas-error';
    }
}
/**
 * Generate WebGL fingerprint
 * GPU and driver information
 */
function getWebGLFingerprint() {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl)
            return 'no-webgl';
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo)
            return 'no-debug-info';
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        return `${vendor}~${renderer}`;
    }
    catch (e) {
        return 'webgl-error';
    }
}
/**
 * Generate audio context fingerprint
 * Audio processing varies by hardware
 */
function getAudioFingerprint() {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext)
            return 'no-audio';
        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const analyser = context.createAnalyser();
        const gainNode = context.createGain();
        const scriptProcessor = context.createScriptProcessor(4096, 1, 1);
        gainNode.gain.value = 0; // Mute
        oscillator.type = 'triangle';
        oscillator.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(gainNode);
        gainNode.connect(context.destination);
        oscillator.start(0);
        const frequencyData = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(frequencyData);
        oscillator.stop();
        context.close();
        // Generate hash from frequency data
        const hash = Array.from(frequencyData.slice(0, 30))
            .map(x => x.toString(36))
            .join('');
        return hash || 'audio-silent';
    }
    catch (e) {
        return 'audio-error';
    }
}
/**
 * Get screen fingerprint
 */
function getScreenFingerprint() {
    return `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
}
/**
 * Detect installed fonts
 * Different systems have different font sets
 */
function getFonts() {
    const testFonts = [
        'Arial', 'Verdana', 'Courier New', 'Georgia', 'Times New Roman',
        'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact',
        'Helvetica', 'Monaco', 'Consolas', 'Lucida Console'
    ];
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return [];
    const detectedFonts = [];
    // Measure base fonts
    const baseWidths = {};
    baseFonts.forEach(baseFont => {
        ctx.font = `${testSize} ${baseFont}`;
        baseWidths[baseFont] = ctx.measureText(testString).width;
    });
    // Test each font
    testFonts.forEach(font => {
        let detected = false;
        baseFonts.forEach(baseFont => {
            ctx.font = `${testSize} '${font}', ${baseFont}`;
            const width = ctx.measureText(testString).width;
            if (width !== baseWidths[baseFont]) {
                detected = true;
            }
        });
        if (detected) {
            detectedFonts.push(font);
        }
    });
    return detectedFonts;
}
/**
 * Get browser plugins
 */
function getPlugins() {
    const plugins = [];
    if (navigator.plugins && navigator.plugins.length > 0) {
        for (let i = 0; i < navigator.plugins.length; i++) {
            plugins.push(navigator.plugins[i].name);
        }
    }
    return plugins;
}
/**
 * Simple hash function for strings
 */
function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
}
/**
 * Generate SHA-256 hash from string
 */
async function sha256(message) {
    try {
        // Use Web Crypto API
        const msgBuffer = new TextEncoder().encode(message);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    }
    catch (e) {
        // Fallback to simple hash if crypto not available
        console.warn('âš ï¸ crypto.subtle not available, using fallback hash');
        return simpleHash(message);
    }
}
/**
 * Generate complete browser fingerprint
 * Returns unique fingerprint ID and all components
 */
export async function generateFingerprint() {
    const canvas = getCanvasFingerprint();
    const webgl = getWebGLFingerprint();
    const audio = getAudioFingerprint();
    const screen = getScreenFingerprint();
    const fonts = getFonts();
    const plugins = getPlugins();
    const components = {
        canvas,
        webgl,
        audio,
        screen,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || null,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        touchSupport: 'ontouchstart' in window,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack || null,
        fonts: fonts.sort(),
        plugins: plugins.sort(),
        userAgent: navigator.userAgent
    };
    // Combine all components into single string
    const fingerprintString = [
        components.canvas,
        components.webgl,
        components.audio,
        components.screen,
        components.timezone,
        components.language,
        components.platform,
        components.hardwareConcurrency,
        components.deviceMemory,
        components.colorDepth,
        components.pixelRatio,
        components.touchSupport,
        components.cookieEnabled,
        components.doNotTrack,
        components.fonts.join(','),
        components.plugins.join(','),
        components.userAgent
    ].join('|');
    // Generate SHA-256 hash
    const fingerprintId = await sha256(fingerprintString);
    // Log key hardware info for debugging
    console.log('ðŸ” Fingerprint hardware info:', {
        platform: components.platform,
        deviceMemory: components.deviceMemory,
        hardwareConcurrency: components.hardwareConcurrency,
        webglVendor: components.webgl.split('~')[0],
        webglRenderer: components.webgl.split('~')[1]?.substring(0, 30)
    });
    return {
        fingerprintId,
        ...components
    };
}
/**
 * Get cached fingerprint from localStorage
 * Fingerprint is cached for 7 days
 */
export function getCachedFingerprint() {
    try {
        const cached = localStorage.getItem('gridchat_fingerprint');
        if (!cached)
            return null;
        const data = JSON.parse(cached);
        const age = Date.now() - data.timestamp;
        const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
        if (age > MAX_AGE) {
            localStorage.removeItem('gridchat_fingerprint');
            return null;
        }
        return data.fingerprint;
    }
    catch (e) {
        return null;
    }
}
/**
 * Cache fingerprint in localStorage
 */
export function cacheFingerprint(fingerprint) {
    try {
        localStorage.setItem('gridchat_fingerprint', JSON.stringify({
            fingerprint,
            timestamp: Date.now()
        }));
    }
    catch (e) {
        console.warn('âš ï¸ Failed to cache fingerprint:', e);
    }
}
/**
 * Get or generate fingerprint
 * Uses cached version if available
 */
export async function getFingerprint() {
    const cached = getCachedFingerprint();
    if (cached) {
        console.log('ðŸ” Using cached fingerprint:', cached.fingerprintId);
        return cached;
    }
    console.log('ðŸ” Generating new fingerprint...');
    const fingerprint = await generateFingerprint();
    cacheFingerprint(fingerprint);
    console.log('ðŸ” Fingerprint generated:', fingerprint.fingerprintId);
    return fingerprint;
}
